/**
 * Field — the one labeling contract for form fields (UX-8, design-system
 * spine).
 *
 * Owns label / required-marker / help / error semantics and the aria ids;
 * the input element stays caller-supplied and caller-styled (the
 * modalFormStyles FLD strings remain the input styling source — Field is
 * deliberately NOT an Input mega-primitive). Layouts: 'stacked' is the
 * modal-form idiom; 'side' is the drawer's fixed-width side-label row
 * (consumed by the UX-11 decomposition).
 *
 * When `htmlFor` is given, the label carries the id `${htmlFor}-label`
 * and help/error render with `${htmlFor}-help` / `${htmlFor}-error` ids.
 * The box beside a Field carries the matching attributes through
 * `fieldBoxAria`: aria-required on a required box, aria-invalid while
 * its error shows, and aria-describedby naming its help and error lines.
 *
 * The required mark (" *") is in the label's own colour and hidden from
 * a screen reader, which hears aria-required on the box instead. The
 * error line is pepper-deep and `role="alert"`; `errorTestId` names it
 * for a test. With `group`, the label is a span (a radiogroup names
 * itself through aria-labelledby, and a label's `for` has nothing to
 * point at).
 *
 * `mode='display'` (UX-13 S0; REPLACES the planned FieldRow, honoring
 * the one-field-primitive intent layout='side' established): a
 * read-only labeled value — label in the read register (12px
 * ink-soft; labels are metadata, metadata is ink-soft), value as
 * children at text-sm ink. Edit-mode props (required/error/htmlFor
 * semantics) do not apply in display mode.
 */

import { type ReactNode } from 'react';
import { FLD_HELP, FLD_LABEL, FLD_REQ } from '@/components/primitives/modalFormStyles';

export interface FieldProps {
  label: string;
  required?: boolean;
  help?: string;
  error?: string;
  layout?: 'stacked' | 'side';
  /** 'edit' (default) labels an input; 'display' renders a read-only labeled value. */
  mode?: 'edit' | 'display';
  /** id of the input this labels; also derives the label/help/error ids. */
  htmlFor?: string;
  /** data-testid on the error line. */
  errorTestId?: string;
  /** The label is a span with the label id and no `for`: for a
   *  radiogroup named through aria-labelledby. */
  group?: boolean;
  children: ReactNode;
}

export interface FieldBoxAria {
  'aria-required'?: 'true';
  'aria-invalid'?: 'true';
  'aria-describedby'?: string;
}

/** The attributes a box beside a Field carries, from the same ids the
 *  Field renders for its help and error lines. */
export function fieldBoxAria(
  htmlFor: string,
  o: { required?: boolean; help?: boolean; error?: boolean },
): FieldBoxAria {
  const described = [o.help ? `${htmlFor}-help` : null, o.error ? `${htmlFor}-error` : null]
    .filter((id): id is string => id !== null)
    .join(' ');
  return {
    ...(o.required ? { 'aria-required': 'true' as const } : {}),
    ...(o.error ? { 'aria-invalid': 'true' as const } : {}),
    ...(described !== '' ? { 'aria-describedby': described } : {}),
  };
}

export function Field({
  label,
  required = false,
  help,
  error,
  layout = 'stacked',
  mode = 'edit',
  htmlFor,
  errorTestId,
  group = false,
  children,
}: FieldProps): ReactNode {
  if (mode === 'display') {
    return (
      <div data-testid="field-display">
        <div className="text-xs text-ink-soft">{label}</div>
        <div className="text-sm text-ink">{children}</div>
      </div>
    );
  }

  const labelId = htmlFor ? `${htmlFor}-label` : undefined;
  const helpId = htmlFor && help ? `${htmlFor}-help` : undefined;
  const errorId = htmlFor && error ? `${htmlFor}-error` : undefined;

  const words = (
    <>
      {label}
      {required && (
        <span className={FLD_REQ} aria-hidden="true">
          {' '}
          *
        </span>
      )}
    </>
  );

  const meta = (
    <>
      {help && (
        <p id={helpId} className={FLD_HELP}>
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" data-testid={errorTestId} className="text-xs text-pepper-deep mt-1.5">
          {error}
        </p>
      )}
    </>
  );

  if (layout === 'side') {
    const sideClass = 'w-[122px] shrink-0 text-[12.5px] font-semibold text-sage-400 tracking-[0.01em] pt-0.5';
    return (
      <div className="flex gap-3 py-2 border-b border-sage-800/[8%]">
        {group ? (
          <span id={labelId} className={sideClass}>
            {words}
          </span>
        ) : (
          <label id={labelId} htmlFor={htmlFor} className={sideClass}>
            {words}
          </label>
        )}
        <div className="flex-1 min-w-0">
          {children}
          {meta}
        </div>
      </div>
    );
  }

  return (
    <div>
      {group ? (
        <span id={labelId} className={FLD_LABEL}>
          {words}
        </span>
      ) : (
        <label id={labelId} htmlFor={htmlFor} className={FLD_LABEL}>
          {words}
        </label>
      )}
      {children}
      {meta}
    </div>
  );
}
